import React from 'react';
import { useTranslation, Trans } from 'react-i18next';

function CookiePolicy() {
  const { t } = useTranslation('pages');
  const p = (key) => `cookiePolicy.${key}`;

  const tableCell = "text-[14px] leading-[22px] px-4 py-3 border border-[#E5E5E3]";
  const tableHeader = "text-[14px] font-semibold leading-[22px] px-4 py-3 border border-[#E5E5E3] bg-[#F8F8F7] text-left";
  const h2 = "text-[20px] font-bold mt-8 mb-4";
  const h3 = "text-[18px] font-semibold mt-6 mb-3";
  const para = "text-[16px] leading-[28px] mb-4";
  const paraEnd = "text-[16px] leading-[28px] mb-6";
  const li = "text-[16px] leading-[28px]";

  return (
    <div className="min-h-safe-screen bg-[#fff]">
      <div className="max-w-3xl mx-auto px-6 py-8 pb-24">
        <h1 className="text-[var(--Color-Grays-Gray-10,#0D0C07)] text-[28px] font-bold leading-[36px] mb-2">
          {t(p('title'))}
        </h1>
        <p className="text-[#878683] text-[14px] mb-8">
          {t(p('lastUpdated'))}
        </p>

        <div className="prose prose-gray max-w-none">

          {/* ─── Introduction ─── */}
          <p className={paraEnd}>
            <Trans i18nKey={p('intro1')} t={t} components={{ strong: <strong /> }} />
          </p>
          <p className={paraEnd}>
            {t(p('intro2'))}
          </p>

          {/* ─── What are cookies ─── */}
          <h2 className={h2}>{t(p('s1Title'))}</h2>
          <p className={para}>
            <Trans i18nKey={p('s1Cookies')} t={t} components={{ strong: <strong /> }} />
          </p>
          <p className={para}>
            <Trans i18nKey={p('s1Storage')} t={t} components={{ strong: <strong /> }} />
          </p>
          <p className={paraEnd}>
            <Trans i18nKey={p('s1Fingerprint')} t={t} components={{ strong: <strong /> }} />
          </p>

          {/* ─── Legal basis ─── */}
          <h2 className={h2}>{t(p('s2Title'))}</h2>
          <p className={para}>{t(p('s2Intro'))}</p>
          <ul className="list-disc pl-6 mb-6 space-y-2">
            <li className={li}><Trans i18nKey={p('s2Essential')} t={t} components={{ strong: <strong /> }} /></li>
            <li className={li}><Trans i18nKey={p('s2Analytics')} t={t} components={{ strong: <strong /> }} /></li>
          </ul>

          {/* ─── Category 1: Strictly Necessary ─── */}
          <h2 className={h2}>{t(p('s3Title'))}</h2>
          <p className={para}>
            <Trans i18nKey={p('s3Intro')} t={t} components={{ strong: <strong /> }} />
          </p>

          <div className="overflow-x-auto mb-6">
            <table className="w-full border-collapse text-left">
              <thead>
                <tr>
                  <th className={tableHeader}>{t(p('tableNameKey'))}</th>
                  <th className={tableHeader}>{t(p('tableType'))}</th>
                  <th className={tableHeader}>{t(p('tablePurpose'))}</th>
                  <th className={tableHeader}>{t(p('tableDuration'))}</th>
                </tr>
              </thead>
              <tbody>
                <tr>
                  <td className={tableCell}><code>authToken</code></td>
                  <td className={tableCell}>localStorage</td>
                  <td className={tableCell}>{t(p('authTokenPurpose'))}</td>
                  <td className={tableCell}>{t(p('authTokenDuration'))}</td>
                </tr>
                <tr>
                  <td className={tableCell}><code>email</code></td>
                  <td className={tableCell}>localStorage</td>
                  <td className={tableCell}>{t(p('emailPurpose'))}</td>
                  <td className={tableCell}>{t(p('emailDuration'))}</td>
                </tr>
                <tr>
                  <td className={tableCell}><code>deviceId</code></td>
                  <td className={tableCell}>localStorage</td>
                  <td className={tableCell}>{t(p('deviceIdPurpose'))}</td>
                  <td className={tableCell}>{t(p('deviceIdDuration'))}</td>
                </tr>
                <tr>
                  <td className={tableCell}><code>9meals_preferences</code></td>
                  <td className={tableCell}>localStorage</td>
                  <td className={tableCell}>{t(p('preferencesPurpose'))}</td>
                  <td className={tableCell}>{t(p('preferencesDuration'))}</td>
                </tr>
                <tr>
                  <td className={tableCell}><code>inProgressMealPlan</code></td>
                  <td className={tableCell}>localStorage</td>
                  <td className={tableCell}>{t(p('inProgressPurpose'))}</td>
                  <td className={tableCell}>{t(p('inProgressDuration'))}</td>
                </tr>
                <tr>
                  <td className={tableCell}><code>oauthReturnPath</code></td>
                  <td className={tableCell}>localStorage</td>
                  <td className={tableCell}>{t(p('oauthReturnPurpose'))}</td>
                  <td className={tableCell}>{t(p('oauthReturnDuration'))}</td>
                </tr>
                <tr>
                  <td className={tableCell}><code>oauthPendingCheckout</code></td>
                  <td className={tableCell}>localStorage</td>
                  <td className={tableCell}>{t(p('oauthPendingPurpose'))}</td>
                  <td className={tableCell}>{t(p('oauthPendingDuration'))}</td>
                </tr>
                <tr>
                  <td className={tableCell}>{t(p('fingerprintName'))}</td>
                  <td className={tableCell}>{t(p('fingerprintType'))}</td>
                  <td className={tableCell}>{t(p('fingerprintPurpose'))}</td>
                  <td className={tableCell}>{t(p('fingerprintDuration'))}</td>
                </tr>
              </tbody>
            </table>
          </div>

          {/* ─── Category 2: Functional ─── */}
          <h2 className={h2}>{t(p('s4Title'))}</h2>
          <p className={para}>{t(p('s4Intro'))}</p>

          <div className="overflow-x-auto mb-6">
            <table className="w-full border-collapse text-left">
              <thead>
                <tr>
                  <th className={tableHeader}>{t(p('tableNameKey'))}</th>
                  <th className={tableHeader}>{t(p('tableType'))}</th>
                  <th className={tableHeader}>{t(p('tablePurpose'))}</th>
                  <th className={tableHeader}>{t(p('tableDuration'))}</th>
                </tr>
              </thead>
              <tbody>
                <tr>
                  <td className={tableCell}><code>i18nextLng</code></td>
                  <td className={tableCell}>localStorage</td>
                  <td className={tableCell}>{t(p('i18nPurpose'))}</td>
                  <td className={tableCell}>{t(p('i18nDuration'))}</td>
                </tr>
                <tr>
                  <td className={tableCell}><code>userCountry</code></td>
                  <td className={tableCell}>localStorage</td>
                  <td className={tableCell}>{t(p('countryPurpose'))}</td>
                  <td className={tableCell}>{t(p('countryDuration'))}</td>
                </tr>
              </tbody>
            </table>
          </div>

          {/* ─── Category 3: Analytics ─── */}
          <h2 className={h2}>{t(p('s5Title'))}</h2>
          <p className={para}>
            <Trans i18nKey={p('s5Intro')} t={t} components={{ strong: <strong /> }} />
          </p>

          {/* Google Analytics */}
          <h3 className={h3}>{t(p('s5gaTitle'))}</h3>
          <p className={para}>{t(p('s5gaProvider'))}</p>
          <div className="overflow-x-auto mb-6">
            <table className="w-full border-collapse text-left">
              <thead>
                <tr>
                  <th className={tableHeader}>{t(p('tableCookie'))}</th>
                  <th className={tableHeader}>{t(p('tablePurpose'))}</th>
                  <th className={tableHeader}>{t(p('tableDuration'))}</th>
                </tr>
              </thead>
              <tbody>
                <tr>
                  <td className={tableCell}><code>_ga</code></td>
                  <td className={tableCell}>{t(p('gaPurpose'))}</td>
                  <td className={tableCell}>{t(p('gaDuration'))}</td>
                </tr>
                <tr>
                  <td className={tableCell}><code>_ga_MRLCHB0FMK</code></td>
                  <td className={tableCell}>{t(p('gaSessionPurpose'))}</td>
                  <td className={tableCell}>{t(p('gaSessionDuration'))}</td>
                </tr>
              </tbody>
            </table>
          </div>
          <p className={para}>
            <Trans
              i18nKey={p('s5gaFooter')}
              t={t}
              components={{
                dpt: <a href="https://privacy.google.com/businesses/processorterms/" className="text-[#006600] underline" target="_blank" rel="noopener noreferrer" />,
                optout: <a href="https://tools.google.com/dlpage/gaoptout" className="text-[#006600] underline" target="_blank" rel="noopener noreferrer" />,
              }}
            />
          </p>

          {/* Amplitude */}
          <h3 className={h3}>{t(p('s5ampTitle'))}</h3>
          <p className={para}>
            <Trans i18nKey={p('s5ampProvider')} t={t} components={{ strong: <strong /> }} />
          </p>
          <div className="overflow-x-auto mb-6">
            <table className="w-full border-collapse text-left">
              <thead>
                <tr>
                  <th className={tableHeader}>{t(p('tableNameKey'))}</th>
                  <th className={tableHeader}>{t(p('tableType'))}</th>
                  <th className={tableHeader}>{t(p('tablePurpose'))}</th>
                  <th className={tableHeader}>{t(p('tableDuration'))}</th>
                </tr>
              </thead>
              <tbody>
                <tr>
                  <td className={tableCell}>{t(p('ampCookieName'))}</td>
                  <td className={tableCell}>Cookie</td>
                  <td className={tableCell}>{t(p('ampCookiePurpose'))}</td>
                  <td className={tableCell}>{t(p('ampCookieDuration'))}</td>
                </tr>
                <tr>
                  <td className={tableCell}><code>amplitude_flow_id</code>, <code>amplitude_flow_id_ts</code></td>
                  <td className={tableCell}>sessionStorage</td>
                  <td className={tableCell}>{t(p('ampFlowPurpose'))}</td>
                  <td className={tableCell}>{t(p('ampFlowDuration'))}</td>
                </tr>
                <tr>
                  <td className={tableCell}><code>amplitude_generation_id</code></td>
                  <td className={tableCell}>sessionStorage</td>
                  <td className={tableCell}>{t(p('ampGenPurpose'))}</td>
                  <td className={tableCell}>{t(p('ampGenDuration'))}</td>
                </tr>
                <tr>
                  <td className={tableCell}><code>amplitude_meal_set_id</code></td>
                  <td className={tableCell}>sessionStorage</td>
                  <td className={tableCell}>{t(p('ampMealPurpose'))}</td>
                  <td className={tableCell}>{t(p('ampMealDuration'))}</td>
                </tr>
              </tbody>
            </table>
          </div>
          <p className={paraEnd}>
            <Trans i18nKey={p('s5ampReplay')} t={t} components={{ strong: <strong /> }} />
          </p>

          {/* Contentsquare */}
          <h3 className={h3}>{t(p('s5csTitle'))}</h3>
          <p className={para}>{t(p('s5csProvider'))}</p>
          <p className={paraEnd}>
            <Trans
              i18nKey={p('s5csDesc')}
              t={t}
              components={{
                csprivacy: <a href="https://contentsquare.com/privacy-center/" className="text-[#006600] underline" target="_blank" rel="noopener noreferrer" />,
              }}
            />
          </p>

          {/* ─── Category 4: Third-party payment ─── */}
          <h2 className={h2}>{t(p('s6Title'))}</h2>
          <p className={paraEnd}>
            <Trans
              i18nKey={p('s6Desc')}
              t={t}
              components={{
                strong: <strong />,
                stripeprivacy: <a href="https://stripe.com/privacy" className="text-[#006600] underline" target="_blank" rel="noopener noreferrer" />,
              }}
            />
          </p>

          {/* ─── Your rights ─── */}
          <h2 className={h2}>{t(p('s7Title'))}</h2>
          <p className={para}>{t(p('s7Intro'))}</p>
          <ul className="list-disc pl-6 mb-6 space-y-2">
            <li className={li}><Trans i18nKey={p('rightInfo')} t={t} components={{ strong: <strong /> }} /></li>
            <li className={li}><Trans i18nKey={p('rightConsent')} t={t} components={{ strong: <strong /> }} /></li>
            <li className={li}><Trans i18nKey={p('rightRevoke')} t={t} components={{ strong: <strong /> }} /></li>
            <li className={li}><Trans i18nKey={p('rightAccess')} t={t} components={{ strong: <strong /> }} /></li>
            <li className={li}><Trans i18nKey={p('rightDelete')} t={t} components={{ strong: <strong /> }} /></li>
            <li className={li}><Trans i18nKey={p('rightPortability')} t={t} components={{ strong: <strong /> }} /></li>
            <li className={li}>
              <Trans
                i18nKey={p('rightComplaint')}
                t={t}
                components={{
                  strong: <strong />,
                  aepd: <a href="https://www.aepd.es/" className="text-[#006600] underline" target="_blank" rel="noopener noreferrer" />,
                }}
              />
            </li>
          </ul>

          {/* ─── How to manage ─── */}
          <h2 className={h2}>{t(p('s8Title'))}</h2>

          <h3 className={h3}>{t(p('s8PanelTitle'))}</h3>
          <p className={para}>{t(p('s8PanelDesc'))}</p>

          <h3 className={h3}>{t(p('s8BrowserTitle'))}</h3>
          <p className={para}>{t(p('s8BrowserDesc'))}</p>
          <ul className="list-disc pl-6 mb-6 space-y-2">
            <li className={li}><Trans i18nKey={p('browserChrome')} t={t} components={{ strong: <strong /> }} /></li>
            <li className={li}><Trans i18nKey={p('browserFirefox')} t={t} components={{ strong: <strong /> }} /></li>
            <li className={li}><Trans i18nKey={p('browserSafari')} t={t} components={{ strong: <strong /> }} /></li>
            <li className={li}><Trans i18nKey={p('browserEdge')} t={t} components={{ strong: <strong /> }} /></li>
          </ul>

          <h3 className={h3}>{t(p('s8LocalTitle'))}</h3>
          <p className={paraEnd}>{t(p('s8LocalDesc'))}</p>

          {/* ─── Cookie duration ─── */}
          <h2 className={h2}>{t(p('s9Title'))}</h2>
          <ul className="list-disc pl-6 mb-6 space-y-2">
            <li className={li}><Trans i18nKey={p('durSession')} t={t} components={{ strong: <strong /> }} /></li>
            <li className={li}><Trans i18nKey={p('durPersistent')} t={t} components={{ strong: <strong /> }} /></li>
            <li className={li}><Trans i18nKey={p('durTemp')} t={t} components={{ strong: <strong />, code: <code /> }} /></li>
            <li className={li}><Trans i18nKey={p('durGA')} t={t} components={{ strong: <strong /> }} /></li>
            <li className={li}><Trans i18nKey={p('durFingerprint')} t={t} components={{ strong: <strong /> }} /></li>
          </ul>

          {/* ─── International transfers ─── */}
          <h2 className={h2}>{t(p('s10Title'))}</h2>
          <p className={paraEnd}>{t(p('s10Intro'))}</p>
          <ul className="list-disc pl-6 mb-6 space-y-2">
            <li className={li}><Trans i18nKey={p('transferGoogle')} t={t} components={{ strong: <strong /> }} /></li>
            <li className={li}><Trans i18nKey={p('transferAmplitude')} t={t} components={{ strong: <strong /> }} /></li>
            <li className={li}><Trans i18nKey={p('transferCS')} t={t} components={{ strong: <strong /> }} /></li>
            <li className={li}><Trans i18nKey={p('transferStripe')} t={t} components={{ strong: <strong /> }} /></li>
          </ul>

          {/* ─── Updates ─── */}
          <h2 className={h2}>{t(p('s11Title'))}</h2>
          <p className={paraEnd}>{t(p('s11Desc'))}</p>

          {/* ─── Contact ─── */}
          <h2 className={h2}>{t(p('s12Title'))}</h2>
          <p className={para}>{t(p('s12Intro'))}</p>
          <p className={paraEnd}>
            Email: <a href="mailto:support@9meals.ai" className="text-[#006600] underline">support@9meals.ai</a>
          </p>
          <p className={paraEnd}>
            <Trans
              i18nKey={p('s12More')}
              t={t}
              components={{
                aboutcookies: <a href="https://www.aboutcookies.org" className="text-[#006600] underline" target="_blank" rel="noopener noreferrer" />,
                allaboutcookies: <a href="https://www.allaboutcookies.org" className="text-[#006600] underline" target="_blank" rel="noopener noreferrer" />,
              }}
            />
          </p>
        </div>
      </div>
    </div>
  );
}

export default CookiePolicy;
